/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * System Health Service - SuperAdmin System Health API
 * ============================================================================
 *
 * Service layer for System Health Dashboard API calls.
 *
 * ============================================================================
 */

import { get, post } from '@/services/api/client';
import { ApiResponse, PagedResponse } from '@/services/api/types';

const BASE_URL = '/admin/system-health';

// Types
export interface ServiceStatus {
  name: string;
  status: 'UP' | 'DOWN' | 'DEGRADED' | 'UNKNOWN';
  details: Record<string, unknown>;
}

export interface DatabasePoolStatus {
  activeConnections: number;
  idleConnections: number;
  maxConnections: number;
  utilizationPercent: number;
}

export interface JvmStatus {
  heapUsedMb: number;
  heapMaxMb: number;
  heapUtilizationPercent: number;
  threadCount: number;
  nonHeapUsedMb: number;
}

export interface RequestMetrics {
  requestCount1m: number;
  errorCount1m: number;
  errorRate: number;
  avgResponseMs: number;
}

export interface OsStatus {
  cpuUsagePercent: number;
  availableProcessors: number;
  totalMemoryMb: number;
  usedMemoryMb: number;
  freeMemoryMb: number;
  memoryUsagePercent: number;
  systemLoadAverage: number;
}

export interface DiskStatus {
  mount: string;
  type: string;
  totalGb: number;
  usedGb: number;
  freeGb: number;
  usagePercent: number;
}

export interface SystemHealth {
  timestamp: string;
  services: Record<string, ServiceStatus>;
  databasePool: DatabasePoolStatus;
  jvm: JvmStatus;
  os: OsStatus;
  disks: DiskStatus[];
  requestMetrics: RequestMetrics;
  uptimeSeconds: number;
  uptimeFormatted: string;
}

export interface HistoryPoint {
  timestamp: string;
  dbActiveConnections: number;
  jvmHeapUsedMb: number;
  requestCount: number;
  errorCount: number;
  avgResponseMs: number;
}

export interface HealthHistory {
  points: HistoryPoint[];
}

export interface SlowRequest {
  id: string;
  requestUri: string;
  requestMethod: string;
  durationMs: number;
  userId: string | null;
  orgId: string | null;
  ipAddress: string | null;
  timestamp: string;
}

/**
 * System Health Service
 */
export const systemHealthService = {
  /**
   * Get current system health status
   */
  async getCurrentHealth(): Promise<SystemHealth> {
    const response = await get<ApiResponse<SystemHealth>>(BASE_URL);
    return response.data;
  },

  /**
   * Get health history for specified hours
   */
  async getHealthHistory(hours: number = 24): Promise<HealthHistory> {
    const response = await get<ApiResponse<HealthHistory>>(`${BASE_URL}/history`, { hours });
    return response.data;
  },

  /**
   * Get slow request logs
   */
  async getSlowRequests(
    page: number = 0,
    size: number = 20,
    thresholdMs?: number
  ): Promise<PagedResponse<SlowRequest>> {
    const params: Record<string, unknown> = { page, size };
    if (thresholdMs) {
      params.thresholdMs = thresholdMs;
    }
    const response = await get<ApiResponse<PagedResponse<SlowRequest>>>(`${BASE_URL}/slow-requests`, params);
    return response.data;
  },

  /**
   * Manually trigger a health snapshot
   */
  async takeSnapshot(): Promise<void> {
    await post<ApiResponse<void>>(`${BASE_URL}/snapshot`, {});
  },
};

export default systemHealthService;
